import { CurrencyCircleDollar, Clock, CreditCard } from 'phosphor-react';
import React, { useEffect, useMemo, useState } from 'react';
import { DillButton, DillCard, DillInput, DillModal, DillSelect, DillToggle } from '../Dill';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { COLORS } from '../../../utils/colors';
import { openDillToast } from '../../../utils/helpers';
import { AppServerResponse } from '../../../utils/interfaces/generalInterfaces';
import {
  getGroupedBuyersSuppliers,
  updateBuyerAutopay,
} from '../../../redux/services/buyersService';
import { updateSelectedSupplierForBuyer } from '../../../redux/globalSlices/invoiceSlice';
import { Buyer } from '@dill/dill-shared';

// ── Frequency options ──
export const frequencyOptions = [
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Due Date', value: 'dueDate' },
];

// ── "On" sub-options per frequency ──
export const onOptionsMap: Record<string, { label: string; value: string }[]> = {
  weekly: [
    { label: 'Monday', value: 'Monday' },
    { label: 'Tuesday', value: 'Tuesday' },
    { label: 'Wednesday', value: 'Wednesday' },
    { label: 'Thursday', value: 'Thursday' },
    { label: 'Friday', value: 'Friday' },
  ],
  monthly: [
    { label: '1st day', value: '1stday' },
    { label: '2nd day', value: '2ndday' },
    { label: '15th day', value: '15thday' },
    { label: 'Last day', value: 'lastday' },
  ],
  dueDate: [
    { label: 'Exact day', value: 'exact' },
    { label: '1 day before', value: '1daybefore' },
    { label: '2 days before', value: '2daysbefore' },
  ],
};

// ── Payment strategies ──
type PaymentStrategy = 'FLAT_AMOUNT' | 'ALL_OPEN' | 'ALL_PAST_DUE' | 'PRIOR_MONTH_STATEMENT';

export const strategyOptions: {
  value: PaymentStrategy;
  label: string;
  description: string;
}[] = [
  {
    value: 'FLAT_AMOUNT',
    label: 'Flat dollar amount',
    description: 'Pay a fixed amount each period, applied to your oldest invoices first',
  },
  {
    value: 'ALL_OPEN',
    label: 'Pay all open invoices',
    description: 'Pay all invoices that are currently open, starting with the oldest',
  },
  {
    value: 'ALL_PAST_DUE',
    label: 'Pay all past due invoices',
    description: 'Pay only invoices past their due date, starting with the oldest',
  },
  {
    value: 'PRIOR_MONTH_STATEMENT',
    label: "Pay off last month's statement",
    description: "Pay last month's invoices oldest first (e.g., all of March on April 1st)",
  },
];

interface AppAutopayModalProps {
  open: boolean;
  supplierName: string;
  buyerName: string;
  handleClose: () => void;
  isAutoPaySelected: boolean;
  onAddNewPayment: () => void;
  buyer?: Buyer | null;
}

export const AppAutopayModal = ({
  open,
  supplierName,
  buyerName,
  handleClose,
  isAutoPaySelected,
  onAddNewPayment,
  buyer,
}: AppAutopayModalProps) => {
  const dispatch = useAppDispatch();
  const { paymentMethods } = useAppSelector((state) => state.payments);
  const { selectedSupplierForBuyer } = useAppSelector((state) => state.invoice);
  const { selectedMainBuyer } = useAppSelector((state) => state.buyers);

  const [isActive, setIsActive] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [frequency, setFrequency] = useState<'weekly' | 'monthly' | 'dueDate'>('monthly');
  const [frequencyOn, setFrequencyOn] = useState('1stday');
  const [strategy, setStrategy] = useState<PaymentStrategy>('ALL_OPEN');
  const [flatAmount, setFlatAmount] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [updating, setUpdating] = useState(false);

  // Seed state from saved details when modal opens
  useEffect(() => {
    if (open) {
      const saved = buyer?.autopayDetails;
      setErrorMessage('');
      setIsActive(isAutoPaySelected);
      setFrequency(saved?.frequency ?? 'monthly');
      setStrategy((saved?.paymentStrategy as PaymentStrategy) ?? 'ALL_OPEN');
      setFlatAmount(saved?.flatAmount != null ? String(saved.flatAmount) : '');
      if (saved?.frequencyOn) {
        setFrequencyOn(saved.frequencyOn);
      }
    }
  }, [isAutoPaySelected, open, buyer]);

  // Derive bank account payment methods
  const bankAccountOptions = useMemo(() => {
    const result: { label: string; value: string }[] = [];
    if (paymentMethods && paymentMethods.length > 0) {
      for (const element of paymentMethods) {
        if (element.type === 'BANK_ACCOUNT' && !element.isDeactivated) {
          result.push({ label: element.paymentMethodName ?? '', value: element.id });
        }
      }
      const savedMethodId = buyer?.autopayDetails?.paymentMethodId;
      const defaultMethod =
        (savedMethodId && paymentMethods.find((m) => m.id === savedMethodId)) ||
        paymentMethods.find((m) => m.type === 'BANK_ACCOUNT');
      if (defaultMethod) {
        setSelectedPaymentMethod(defaultMethod.id);
      }
    }
    return result;
  }, [paymentMethods, buyer]);

  // Update "on" sub-options when frequency changes
  const onOptions = useMemo(() => onOptionsMap[frequency] ?? [], [frequency]);

  useEffect(() => {
    if (onOptions.length > 0 && !onOptions.some((o) => o.value === frequencyOn)) {
      setFrequencyOn(onOptions[0].value);
    }
  }, [onOptions]);

  // "Pay by due date" can only target invoices due today (± the offset). Past-due and prior-month
  // invoices fall outside that window, so those strategies would never pay anything and the backend
  // rejects them — hide them when the frequency is "Due Date".
  const availableStrategyOptions = useMemo(
    () =>
      frequency === 'dueDate'
        ? strategyOptions.filter((o) => o.value === 'ALL_OPEN' || o.value === 'FLAT_AMOUNT')
        : strategyOptions,
    [frequency]
  );

  useEffect(() => {
    if (!availableStrategyOptions.some((o) => o.value === strategy)) {
      setStrategy('ALL_OPEN');
    }
  }, [availableStrategyOptions]);

  const handleSave = async () => {
    if (selectedPaymentMethod === '') {
      setErrorMessage('Add a bank account method to setup Autopay');
      return;
    }
    if (strategy === 'FLAT_AMOUNT') {
      const parsed = parseFloat(flatAmount);
      if (!flatAmount || isNaN(parsed) || parsed <= 0) {
        setErrorMessage('Enter a valid dollar amount');
        return;
      }
    }
    setErrorMessage('');

    if (!selectedMainBuyer?.id || !buyer?.id) {
      return;
    }

    setUpdating(true);
    const result = await dispatch(
      updateBuyerAutopay({
        mainBuyerId: selectedMainBuyer.id,
        buyerId: buyer.id,
        isAutopayActive: isActive,
        autopayDetails: {
          paymentMethodId: selectedPaymentMethod,
          frequency,
          frequencyOn,
          paymentStrategy: strategy,
          flatAmount: strategy === 'FLAT_AMOUNT' ? parseFloat(flatAmount) : null,
        },
      })
    );
    setUpdating(false);

    if (result.type === 'buyers/updateBuyerAutopay/fulfilled') {
      const payload = result.payload as AppServerResponse | null;
      if (payload?.data && selectedSupplierForBuyer) {
        dispatch(
          updateSelectedSupplierForBuyer({
            ...selectedSupplierForBuyer,
            buyer: payload.data,
          })
        );
      }
      dispatch(getGroupedBuyersSuppliers({ mainBuyerId: selectedMainBuyer.id, weeksLimit: 2 }));
      openDillToast({ message: 'Autopay settings saved', type: 'SUCCESS' });
      handleClose();
    } else {
      openDillToast({
        message: (result.payload as string) || 'Failed to save autopay settings',
        type: 'ERROR',
      });
    }
  };

  return (
    <DillModal
      open={open}
      onClose={handleClose}
      title="Autopay Setup"
      subtitle={supplierName}
      maxWidth="560px"
      contentClassName="flex-1 min-h-0 overflow-y-auto px-5 py-5 flex flex-col gap-4"
      footer={
        <>
          <DillButton
            label="Cancel"
            variant="secondary"
            onClick={handleClose}
            isDisabled={updating}
          />
          <DillButton
            label="Save changes"
            variant="primary"
            onClick={handleSave}
            isLoading={updating}
          />
        </>
      }>
      {/* Section 1: Toggle + Payment Method */}
      <DillCard className="bg-white">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-green-50">
            <CurrencyCircleDollar size={16} color={COLORS.SUCCESS_700} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900">Autopay</h4>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
              To: <span className="font-medium text-gray-700">{supplierName}</span> &middot; From:{' '}
              <span className="font-medium text-gray-700">{buyerName}</span>
            </p>
          </div>
        </div>

        {/* Active toggle */}
        <div className="mt-4">
          <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Enable autopay</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Automatically pay invoices from this supplier
              </p>
            </div>
            <DillToggle value={isActive} onChange={setIsActive} />
          </div>
        </div>

        {/* Bank account method */}
        <div className="mt-4">
          <p className="text-xs font-medium text-gray-600 mb-2">Bank Account Method</p>
          <DillSelect
            value={selectedPaymentMethod}
            onChange={setSelectedPaymentMethod}
            options={bankAccountOptions}
            placeholder="Select a bank account"
          />
          <button
            type="button"
            onClick={onAddNewPayment}
            className="mt-2 text-xs text-blue-500 hover:text-blue-700 underline cursor-pointer bg-transparent border-none p-0">
            + Add New Method
          </button>
        </div>
      </DillCard>

      {/* Section 2: Frequency (shown when enabled) */}
      {isActive && (
        <DillCard className="bg-white">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-50">
              <Clock size={16} color={COLORS.PRIMARY_500} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900">Frequency</h4>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                Choose how often autopay runs.
              </p>
            </div>
          </div>

          <div className="mt-4 flex gap-3">
            <div className="flex-1">
              <p className="text-xs font-medium text-gray-600 mb-2">When</p>
              <DillSelect
                value={frequency}
                onChange={(v) => setFrequency(v as typeof frequency)}
                options={frequencyOptions}
              />
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-gray-600 mb-2">On</p>
              <DillSelect
                value={frequencyOn}
                onChange={setFrequencyOn}
                options={onOptions}
                disabled={frequency === 'dueDate'}
              />
            </div>
          </div>
        </DillCard>
      )}

      {/* Section 3: Payment Strategy (shown when enabled) */}
      {isActive && (
        <DillCard className="bg-white">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-purple-50">
              <CreditCard size={16} color="#7c3aed" weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900">Payment Strategy</h4>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                Choose which invoices get paid each period.
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-2">
            {availableStrategyOptions.map((opt) => (
              <div
                key={opt.value}
                onClick={() => setStrategy(opt.value)}
                className={`flex items-start gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors ${
                  strategy === opt.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}>
                {/* Radio circle */}
                <div className="mt-0.5">
                  <div
                    className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                      strategy === opt.value ? 'border-blue-500' : 'border-gray-300'
                    }`}>
                    {strategy === opt.value && <div className="h-2 w-2 rounded-full bg-blue-500" />}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{opt.description}</p>
                </div>
              </div>
            ))}

            {/* Flat amount input */}
            {strategy === 'FLAT_AMOUNT' && (
              <div className="ml-7 mt-1">
                <div className="flex items-center gap-2 max-w-[220px]">
                  <span className="text-sm text-gray-500">$</span>
                  <DillInput
                    value={flatAmount}
                    onChange={(v) => {
                      setFlatAmount(v);
                      setErrorMessage('');
                    }}
                    placeholder="0.00"
                    type="number"
                  />
                </div>
              </div>
            )}
          </div>
        </DillCard>
      )}

      {/* Error */}
      {errorMessage && <p className="text-xs text-red-500 m-0 px-1">{errorMessage}</p>}
    </DillModal>
  );
};
